import * as React from 'react';
import { useMemo } from 'react';
import { Layers, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getScopeIdsByLevel, getScopesByLevel, joinStrings } from '@klareospace/pharos-lib-utils';
import { ROLE_SCOPE, type ResolvedUserScope, type UserScope } from '@klareospace/pharos-lib-types';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_VISIBLE = 2;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveParentSpaceNames(resolvedScopes: ResolvedUserScope[], siteIds: Set<string>): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const s of resolvedScopes) {
    if (s.level === ROLE_SCOPE.SITE && siteIds.has(s.id) && s.parentSpaceName != null && !seen.has(s.parentSpaceName)) {
      seen.add(s.parentSpaceName);
      names.push(s.parentSpaceName);
    }
  }
  return names;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface UserScopeCellProps {
  context?: 'tenant' | 'space' | 'site';
  resolvedScopes: ResolvedUserScope[];
  roles: string[];
  scopes: UserScope[];
}

export function UserScopeCell({ context, resolvedScopes, roles, scopes }: UserScopeCellProps): React.JSX.Element {
  const { t } = useTranslation('settings');

  const nameMap = useMemo(() => new Map(resolvedScopes.map((s) => [s.id, s.name])), [resolvedScopes]);

  const hasTenantScope = scopes.some((s) => s.level === ROLE_SCOPE.TENANT);
  const hasRoles = roles.length > 0;
  const hasFullAccess = hasTenantScope || (hasRoles && scopes.length === 0);

  if (hasFullAccess) {
    return <FullAccessCell allSitesLabel={t('common.allSites')} allSpacesLabel={t('users.scope.allSpaces')} />;
  }

  if (scopes.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  const spaceScopes = getScopesByLevel(scopes, ROLE_SCOPE.SPACE);
  const siteScopes = getScopesByLevel(scopes, ROLE_SCOPE.SITE);
  const hasSpaceScopesOnly = spaceScopes.length > 0 && siteScopes.length === 0;

  if (context === 'space' || context === 'site') {
    return (
      <ScopedContextCell
        hasSpaceScopesOnly={hasSpaceScopesOnly}
        nameMap={nameMap}
        siteScopes={siteScopes}
        sitesLabel={t('common.allSites')}
      />
    );
  }

  return (
    <TenantContextCell
      nameMap={nameMap}
      resolvedScopes={resolvedScopes}
      scopes={scopes}
      siteScopes={siteScopes}
      sitesLabel={t('common.allSites')}
      spaceScopes={spaceScopes}
    />
  );
}

// ─── Private sub-components ───────────────────────────────────────────────────

interface FullAccessCellProps {
  allSitesLabel: string;
  allSpacesLabel: string;
}

function FullAccessCell({ allSitesLabel, allSpacesLabel }: FullAccessCellProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <ScopeRow icon={<Layers className="h-3 w-3" />} label={allSpacesLabel} />
      <ScopeRow icon={<Store className="h-3 w-3" />} label={allSitesLabel} />
    </div>
  );
}

interface ScopedContextCellProps {
  hasSpaceScopesOnly: boolean;
  nameMap: Map<string, string>;
  siteScopes: UserScope[];
  sitesLabel: string;
}

function ScopedContextCell({
  hasSpaceScopesOnly,
  nameMap,
  siteScopes,
  sitesLabel,
}: ScopedContextCellProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <SitesRow
        hasSpaceScopesOnly={hasSpaceScopesOnly}
        nameMap={nameMap}
        siteScopes={siteScopes}
        sitesLabel={sitesLabel}
      />
    </div>
  );
}

interface TenantContextCellProps {
  nameMap: Map<string, string>;
  resolvedScopes: ResolvedUserScope[];
  scopes: UserScope[];
  siteScopes: UserScope[];
  sitesLabel: string;
  spaceScopes: UserScope[];
}

function TenantContextCell({
  nameMap,
  resolvedScopes,
  scopes,
  siteScopes,
  sitesLabel,
  spaceScopes,
}: TenantContextCellProps): React.JSX.Element {
  const hasSpaceScopesOnly = spaceScopes.length > 0 && siteScopes.length === 0;

  const derivedParentSpaceNames =
    spaceScopes.length === 0 && siteScopes.length > 0
      ? deriveParentSpaceNames(resolvedScopes, new Set(getScopeIdsByLevel(scopes, ROLE_SCOPE.SITE)))
      : [];

  return (
    <div className="flex flex-col gap-0.5">
      {spaceScopes.length > 0 && (
        <ScopeNamesRow
          icon={<Layers className="h-3 w-3" />}
          names={spaceScopes.map((s) => nameMap.get(s.id) ?? s.id)}
        />
      )}
      {derivedParentSpaceNames.length > 0 && (
        <ScopeNamesRow icon={<Layers className="h-3 w-3" />} names={derivedParentSpaceNames} />
      )}
      <SitesRow
        hasSpaceScopesOnly={hasSpaceScopesOnly}
        nameMap={nameMap}
        siteScopes={siteScopes}
        sitesLabel={sitesLabel}
      />
    </div>
  );
}

interface SitesRowProps {
  hasSpaceScopesOnly: boolean;
  nameMap: Map<string, string>;
  siteScopes: UserScope[];
  sitesLabel: string;
}

function SitesRow({ hasSpaceScopesOnly, nameMap, siteScopes, sitesLabel }: SitesRowProps): React.JSX.Element | null {
  if (hasSpaceScopesOnly) {
    return <ScopeRow icon={<Store className="h-3 w-3" />} label={sitesLabel} />;
  }
  if (siteScopes.length > 0) {
    return (
      <ScopeNamesRow icon={<Store className="h-3 w-3" />} names={siteScopes.map((s) => nameMap.get(s.id) ?? s.id)} />
    );
  }
  return null;
}

interface ScopeNamesRowProps {
  icon: React.ReactNode;
  names: string[];
}

function ScopeNamesRow({ icon, names }: ScopeNamesRowProps): React.JSX.Element {
  const visible = names.slice(0, MAX_VISIBLE);
  const overflow = names.length - MAX_VISIBLE;
  const visibleText = joinStrings(visible);
  const fullText = joinStrings(names);

  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      {icon}
      <span className="truncate" title={fullText}>
        {visibleText}
      </span>
      {overflow > 0 && (
        <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] font-medium leading-none">+{overflow}</span>
      )}
    </div>
  );
}

interface ScopeRowProps {
  icon: React.ReactNode;
  label: string;
}

function ScopeRow({ icon, label }: ScopeRowProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      {icon}
      <span className="truncate" title={label}>
        {label}
      </span>
    </div>
  );
}
